/**
 * O estado da sessao, e nada mais.
 *
 * **A sessao nao existe no navegador.** Ela e o cookie `HttpOnly`; este modulo
 * guarda so o que a API disse sobre quem e, e sabe que esse dado pode estar
 * velho. Por isso `carregar()` sempre pergunta de novo em vez de confiar num
 * valor guardado -- um valor guardado seria estado que ninguem pode invalidar,
 * que e exatamente o defeito que o logout tem que evitar.
 */

import { eu, ErroDaApi } from "./api";
import type { Usuario } from "./tipos";

export type Estado = "carregando" | "anonimo" | "autenticado" | "sem-conexao";

let usuario: Usuario | null = null;
let estado: Estado = "carregando";
let avisando: (() => void) | null = null;

export function sessaoAtual(): Estado {
  return estado;
}

export function usuarioAtual(): Usuario | null {
  return usuario;
}

export function observar(novo: () => void): void {
  avisando = novo;
}

function publica(): void {
  avisando?.();
}

/**
 * Define a sessao apos um login, um convite ou uma recuperacao.
 *
 * **`publicar = false` e o uso normal depois de um login.** Publicar dispara um
 * redesenho na hora, e o redesenho **troca o `innerHTML` do formulario que o
 * proprio handler ainda esta lendo**: o `#senha` deixa de existir no documento e o
 * `campo("senha")` estoura. Foi assim que o login do admin terminava na tela da
 * casa em vez do setup do TOTP -- o erro nao era o destino, era o espaco em
 * branco entre os dois.
 *
 * Quem faz o redesenho e o `trocar(destino)` do final do handler, uma vez, no
 * lugar certo. Publicar e para mudanca de sessao que vem de **fora** da tela.
 */
export function definir(
  estadoNovo: Estado,
  usuarioNovo: Usuario | null = null,
  publicar = true,
): void {
  estado = estadoNovo;
  usuario = usuarioNovo;
  if (publicar) {
    publica();
  }
}

/**
 * Pergunta a API quem e. **Volta a `true` quando a sessao serve**, que e o que o
 * boot precisa saber para escolher entre a casa e a tela de acesso.
 *
 * **A casa fora do ar nao e a pessoa deslogada.** Sao situacoes diferentes, e
 * mostrar a tela de acesso na primeira faz a pessoa achar que foi desconectada
 * da conta dela -- e, se ela digitar a senha num proxy captive, entrega a senha
 * para quem estiver no meio. O estado `sem-conexao` existe para isso: a tela
 * diz que a casa nao respondeu, e oferece tentar de novo.
 */
export async function carregar(): Promise<boolean> {
  try {
    const quem = await eu();
    definir(quem === null ? "anonimo" : "autenticado", quem);
    return quem !== null;
  } catch (erro) {
    if (erro instanceof ErroDaApi && erro.status === 401) {
      definir("anonimo");
      return false;
    }
    // **Nem 401 nem erro de rede e "sem sessao".** O `catch` aqui e o de rede.
    definir("sem-conexao");
    return false;
  }
}

/**
 * Pergunta a API quem e, **sem publicar** -- e a metade de `carregar()` que nao
 * dispara redesenho.
 *
 * **E para a confirmação do TOTP, e nao para o boot.** O login resolve o
 * usuario uma vez; confirmar o segundo fator muda `precisa_configurar_totp`, e
 * sem um refresh a casa mostra "pendente" mesmo depois de tudo confirmado.
 *
 * `carregar()` publica (`definir` com `publicar = true` default), e publicar
 * aqui redesenha a rota atual -- que, no fluxo de confirmacao, e `/totp`, e
 * redesenhar `/totp` pede a API e redesenha. Com `publicar = false` o refresh
 * atualiza a memoria sem virar no laco: a proxima navegacao (a casa, o mapa, o
 * que for) le o valor fresco.
 */
export async function recarregarUsuario(): Promise<boolean> {
  try {
    const quem = await eu();
    definir(quem === null ? "anonimo" : "autenticado", quem, false);
    return quem !== null;
  } catch (erro) {
    if (erro instanceof ErroDaApi && erro.status === 401) {
      definir("anonimo", null, false);
      return false;
    }
    return false;
  }
}